// A deck's exports in its canvas header (the chat's canvas and the saved deck's own page): PDF, every slide as a page
// exactly as designed, and PowerPoint, each slide as a picture with its speaker notes. The menu says what each file
// holds. Each export is made from the saved version on request (src/apps/tracer/deck-export-route.mjs).
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '@/components/ui/menu';
import { useNamespaceCopy } from '@/i18n/ns';
import buildWatchCopy from '@/i18n/ns/build-watch';
import { apiUrl, authHeaders } from '@/lib/shell';
import { useSessionBoundFetch } from '@/lib/use-session-guard';
import { CanvasHeaderTools } from '../../canvas-footer';
import { WorkspaceIcon } from '../../icons';

export type DeckVersion = {
  artifactId: string;
  versionId: string;
  revisionId: string;
  purpose: 'editor' | 'viewer';
};
type Format = 'pdf' | 'pptx';

/** The deck's title as a file name: the characters a file system refuses become spaces. */
export function deckFileName(title: string, format: Format) {
  const name = [...title]
    .map((ch) => ('\\/:*?"<>|'.includes(ch) || ch.charCodeAt(0) < 32 ? ' ' : ch))
    .join('')
    .trim()
    .slice(0, 120);
  return `${name || 'Deck'}.${format}`;
}

export function DeckExport({ title, version }: { title: string; version: DeckVersion }) {
  const copy = useNamespaceCopy(buildWatchCopy);
  const sessionFetch = useSessionBoundFetch();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  async function exportAs(format: Format) {
    setBusy(true);
    setFailed(false);
    try {
      const response = await sessionFetch(
        apiUrl(`/api/v1/company/apps/${encodeURIComponent(version.artifactId)}/export`),
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            format,
            versionId: version.versionId,
            revisionId: version.revisionId,
            purpose: version.purpose,
          }),
        },
      );
      if (!response.ok) throw new Error('deck_export_unavailable');
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = deckFileName(title, format);
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }
  return (
    <CanvasHeaderTools>
      {failed && (
        <span className="ap-deck-export-failed" role="alert">
          {copy.deckExportFailed}
        </span>
      )}
      <Menu>
        <MenuTrigger asChild>
          <Button type="button" variant="ghost" size="sm" disabled={busy} aria-busy={busy}>
            <WorkspaceIcon name="download" />
            {busy ? copy.deckExporting : copy.deckExport}
          </Button>
        </MenuTrigger>
        <MenuContent label={copy.deckExport} className="ap-deck-export-menu">
          <MenuItem description={copy.deckExportPdfHint} onSelect={() => void exportAs('pdf')}>
            {copy.deckExportPdf}
          </MenuItem>
          <MenuItem description={copy.deckExportPptxHint} onSelect={() => void exportAs('pptx')}>
            {copy.deckExportPptx}
          </MenuItem>
        </MenuContent>
      </Menu>
    </CanvasHeaderTools>
  );
}
